import { Component, EventEmitter, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FeatureType } from 'src/app/components/featured-plates/featured-plates.component';
import {
  PlateCarConfig,
  PlateViewerConfig,
} from 'src/app/models/plateViewerConfig';
import { AdminService } from 'src/app/services/admin-service';
import { ImageKeyPair, ImageService } from 'src/app/services/image-service';

import { carFitsPlate, resolvePresentationAsset } from '../../utils/presentation-asset';
import { VIEWER_CAR_META } from '../../consts/viewer-car-meta';
@Component({
  selector: 'pla-plate-viewer-page',
  templateUrl: './plate-viewer-page.component.html',
  styleUrls: ['./plate-viewer-page.component.scss'],
})
export class PlateViewerPageComponent implements OnInit {
  public searchTrigger: EventEmitter<void> = new EventEmitter<void>();
  public showFeatured: boolean = false;
  public featuredGroup: string = 'christmas';
  public featuredTitle: string = 'Christmas 🎅';

  public raFeatured = new FeatureType('Recently Added', 'recently-added');
  public rsFeatured = new FeatureType('Recently Sold', 'recently-sold');
  public alphabet: string = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  public selectedImage: ImageKeyPair = null;
  public selectedConfig: PlateViewerConfig;
  public registrationString: string = '';
  public configs: PlateViewerConfig[] = [];
  private quickConfig: PlateViewerConfig;
  public lastAlphabetToView: string = 'A';
  private defaultPlate: string = 'PLA 73X';
  private pageType: string = null;
  private inputString: string = null;

  constructor(
    private imageService: ImageService,
    private adminService: AdminService,
    private route: ActivatedRoute
  ) {
    this.pageType = this.route.snapshot.params['type'];
    this.inputString = this.route.snapshot.params['input'];
    const plateIfCar = this.route.snapshot.params['plate'];
    if (this.pageType != null && this.inputString != null) {
      if (this.pageType === 'plate') {
        this.registrationString = this.inputString;
      } else {
        if (plateIfCar !== null) this.registrationString = plateIfCar;
      }
    }
  }

  ngOnInit(): void {
    this.GetConfigs();
    this.generateQuickImage(this.defaultPlate);
    if (
      this.pageType !== null &&
      this.inputString !== null
    ) {
      if (this.pageType === 'car') {
        return; // handled later
      }

      if (this.pageType === 'plate') {
        setTimeout(() => {
          this.UpdateInput(this.inputString);
        }, 100);
        return;
      }
    }
  }

  private ConfigsFetched(): void {
    if (this.pageType != null && this.inputString != null) {
      if (this.pageType === 'car') {
        const toSet = this.configs.find(
          (p) =>
            p.carName.toLowerCase().indexOf(this.inputString.toLowerCase()) > -1
        );
        if (toSet === undefined) {
          console.log('car to set', toSet);
          return;
        }
        this.updateConfig(toSet);
      }
    }
  }

  public LetterInView(
    event: { target: HTMLElement; visible: boolean },
    letter: any
  ): void {
    if (event.visible) this.lastAlphabetToView = letter;
  }

  private GetConfigs(): void {
    this.adminService
      .getImageConfigGroups() // this is unique groups
      .subscribe((_configs: PlateViewerConfig[]) => {
        _configs = _configs.map((c: PlateViewerConfig) => {
          var _ = new PlateViewerConfig(c);
          return _;
        });

        this.configs = _configs.sort((a, b) =>
          a.carName.localeCompare(b.carName)
        );

        this.ConfigsFetched();
      });
  }

  private generateQuickImage(reg: string): void {
    this.quickConfig = this.imageService.getQuickConfig();
    // Legal dated-plate rule: never open on a car too old for this plate.
    if (this.quickConfig && !carFitsPlate(this.quickConfig.id, reg)) {
      this.quickConfig =
        resolvePresentationAsset(reg).config || this.quickConfig;
    }
    this.imageService.INIT();
    this.imageService.quickBuild(
      this.quickConfig,
      reg,
      (image: ImageKeyPair) => {
        this.selectedImage = image;
      }
    );
  }

  // Legal dated-plate rule: once a plate is entered, hide cars too old for it.
  // The demo plate never filters, so the whole catalogue shows by default.
  private fitsEntered(p: PlateViewerConfig): boolean {
    return !this.registrationString || carFitsPlate(p.id, this.registrationString);
  }

  // Approximate build years and type under each car, e.g. "2023 on · Performance".
  public carMetaLabel(p: PlateViewerConfig): string {
    const m = VIEWER_CAR_META[p.id];
    if (!m) return '';
    const years = m.yearTo == null ? `${m.yearFrom} on` : `${m.yearFrom} to ${m.yearTo}`;
    const type = m.type === 'suv' ? 'SUV' : m.type.charAt(0).toUpperCase() + m.type.slice(1);
    return `${years} · ${type}`;
  }

  public ImagesForFeatured(): PlateViewerConfig[] {
    return this.configs.filter(
      (p) => p.groupId === this.featuredGroup && this.fitsEntered(p)
    );
  }

  public ImagesForLetter(letter: string): PlateViewerConfig[] {
    if (this.configs === undefined || this.configs.length === 0) return [];
    return this.configs.filter(
      (p) =>
        p.carName.startsWith(letter) &&
        (this.showFeatured ? p.groupId !== this.featuredGroup : true) &&
        this.fitsEntered(p)
    );
  }

  public updateConfig(config: PlateViewerConfig): void {
    this.selectedConfig = config;
    this.UpdateImage();
  }

  public UpdateInput(input: string): void {
    this.registrationString = input.substring(0, 8);
    this.UpdateImage();
  }

  public UpdateImage(): void {
    var stringToRender = this.registrationString
    if (stringToRender == undefined || stringToRender == '') stringToRender = this.defaultPlate;
    if (window.innerWidth > 768)
      window.scrollTo({ top: 0, behavior: 'smooth' });
    // A newly entered plate can be too new for the chosen car: drop back to a
    // car that fits rather than showing an illegal pairing.
    if (this.selectedConfig && !carFitsPlate(this.selectedConfig.id, stringToRender))
      this.selectedConfig = null;
    if (this.quickConfig && !carFitsPlate(this.quickConfig.id, stringToRender))
      this.quickConfig = resolvePresentationAsset(stringToRender).config || this.quickConfig;
    if (this.selectedConfig == null) {
      this.imageService.quickBuild(
        this.quickConfig,
        stringToRender,
        (image: ImageKeyPair) => {
          this.selectedImage = image;
        }
      );
      return;
    }
    this.imageService.generateImage(
      this.selectedConfig,
      stringToRender,
      (_: ImageKeyPair) => {
        this.selectedImage = _;
      }
    );
  }
}
